import React, { useMemo, ReactElement, useEffect } from "react";

import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { Spin } from "antd";

import {
  ActivationContentProps,
  ActivationFailedContentProps,
  ActivationFormContentProps,
  ActivationTextContentProps,
  ActivationVerifiedContentProps,
} from "./activationContent.model";
import { Button } from "@companion-core/shared/app/Components/Button";
import { FontIcon } from "@companion-core/shared/app/Components/FontIcon";
import { IndependentDynamicForm } from "@companion-core/shared/app/Components/DynamicForm/IndependentDynamicForm";
import { useTrackingStore } from "@companion-core/shared/app/Store/trackingStore";
import { Field } from "@companion-core/shared/app/Interfaces/wizard";
import {
  trackClickRegister,
  trackStepRegister,
} from "@companion-core/shared/app/tracking/register";
import { getModule } from "@companion-core/shared/app/Utils/config";
import dayjs from "dayjs";
import { sanitizeHtml } from "@companion-core/shared/app/Utils/htmlSanitizer";
import { loadFormFromPath } from "@companion-core/shared/app/Utils/dynamicForm";

import "@companion-core/web/src/Assets/Styles/scss/register.scss";

const WaitingForVerificationContent = (): ReactElement => (
  <div data-testid="activation__waiting-for-verification">
    <Spin data-testid="activation__loader" size="large" />
  </div>
);

const VerifiedContent = ({ t, redirectCallback }: ActivationVerifiedContentProps): ReactElement => {
  useEffect(() => {
    trackStepRegister("email_verification_confirmed");
  }, []);

  return (
    <div data-testid="activation__verified" className="activation__success">
      <FontIcon
        data-testid="activation__verified--icon"
        className="activation__success-icon"
        name="JP-Circle-check"
        size={40}
      />
      <h2
        data-testid="activation__verified--title"
        className="activation__success-title super-title-3">
        {t("register.activation.verified.title")}
      </h2>
      <Button
        testID="activation__verified--button"
        classNames={["activation__success-button"]}
        label={t("register.activation.verified.button.label")}
        size="full"
        onClick={() => {
          trackClickRegister("email_verification_next");
          redirectCallback();
        }}
      />
    </div>
  );
};

const VerifyExpiredContent = ({ t }: ActivationTextContentProps): ReactElement => {
  const navigate = useNavigate();

  useEffect(() => {
    trackStepRegister("email_verification_expired");
  }, []);

  return (
    <div data-testid="activation__verify-expired" className="activation__verify-expired">
      <h2
        data-testid="activation__verify-expired--title"
        className="activation__title super-title-3">
        {t("register.activation.verify-expired.title")}
      </h2>
      <div data-testid="activation__verify-expired--body" className="activation__body body-text-4">
        <div className="activation__body-text">{t("register.activation.verify-expired.text")}</div>
        <div className="activation__body-tips">{t("register.activation.verify-expired.tips")}</div>
      </div>
      <Button
        testID="activation__verify-expired--button"
        classNames={["activation__verify-expired-button"]}
        label={t("register.activation.verify-expired.button.label")}
        size="full"
        onClick={() => {
          trackClickRegister("email_verification_expired_retry");
          navigate("/register/member");
        }}
      />
    </div>
  );
};

const IdentityContent = ({
  name,
  redirectCallback,
  initialFormJSON,
}: ActivationFormContentProps): ReactElement => (
  <div data-testid="activation__identity" className="activation__identity">
    {initialFormJSON && (
      <IndependentDynamicForm
        name={name}
        initialFormJSON={initialFormJSON}
        onSubmitForm={(payload: Record<string, unknown>) => {
          trackClickRegister("confirmation_id_submit");
          redirectCallback(payload);
        }}
      />
    )}
  </div>
);

const ActivationFormContent = ({
  name,
  redirectCallback,
  initialFormJSON,
}: ActivationFormContentProps): ReactElement => (
  <div data-testid="activation__activation" className="activation__activation">
    {initialFormJSON && (
      <IndependentDynamicForm
        name={name}
        initialFormJSON={initialFormJSON}
        onSubmitForm={(payload: Record<string, unknown>) => {
          trackClickRegister("validation_submit");
          redirectCallback(payload);
        }}
      />
    )}
  </div>
);

const ActivationSuccessContent = ({ t }: ActivationTextContentProps): ReactElement => {
  const navigate = useNavigate();
  useEffect(() => {
    trackStepRegister("validation");
  }, []);

  return (
    <div data-testid="activation__successful-activation" className="activation__success">
      <FontIcon
        data-testid="activation__successful-activation--icon"
        className="activation__success-icon"
        name="JP-Circle-check"
        size={40}
      />
      <h2
        data-testid="activation__successful-activation--title"
        className="activation__success-title super-title-3">
        {t("register.activation.successful.title")}
      </h2>
      <p
        data-testid="activation__success--body"
        className="activation__body activation__body--centered body-text-4"
        dangerouslySetInnerHTML={{
          // nosemgrep: typescript.react.security.audit.react-dangerouslysetinnerhtml.react-dangerouslysetinnerhtml
          __html: sanitizeHtml(t("register.activation.successful.text")),
        }}
      />
      <Button
        testID="activation__successful-activation--button"
        classNames={["activation__success-button"]}
        label={t("register.activation.successful.button.label")}
        size="full"
        onClick={() => {
          trackClickRegister("validation_login");
          navigate("/login");
        }}
      />
    </div>
  );
};

const ActivationFailedContent = ({
  t,
  redirectCallback,
  activationError,
}: ActivationFailedContentProps): ReactElement => {
  useEffect(() => {
    trackStepRegister("validation_failed");
  }, []);

  return (
    <div data-testid="activation__failed" className="activation__failed">
      <h2
        data-testid="activation__failed--title"
        className="activation__failed-title super-title-3">
        {t("register.activation.failed.title")}
      </h2>
      <div data-testid="activation__failed--body" className="activation__body body-text-4">
        <div className="activation__body-text">
          {t(`register.activation.failed.${activationError}.text`)}
        </div>
        <div className="activation__body-tips">
          {t(`register.activation.failed.${activationError}.tips`)}
        </div>
      </div>
      <Button
        testID="activation__failed--button"
        classNames={["activation__failed-button"]}
        label={t("register.activation.failed.button.label")}
        size="full"
        onClick={() => {
          trackClickRegister("validation_failed_retry");
          redirectCallback();
        }}
      />
    </div>
  );
};

export const ActivationContent = ({
  state,
  redirectCallback,
  memberData,
  activationErrorCode,
}: ActivationContentProps) => {
  const { t } = useTranslation();
  const formPath = useMemo(() => getModule("register")?.modules?.activation?.formPath ?? "", []);
  const identityFormPath = useMemo(
    () => getModule("register")?.modules?.activation?.identityFormPath ?? "",
    [],
  );
  const identityForm = useMemo(() => {
    const identityJsonForm = loadFormFromPath(identityFormPath);
    identityJsonForm?.steps[0]?.fields
      .filter((field: Field) => memberData?.[field.name])
      .forEach((field: Field) => {
        if (field.type === "date") {
          field.value = dayjs(memberData?.[field.name] as string, "YYYY-MM-DD");
        } else {
          field.value = memberData?.[field.name];
        }
      });
    return identityJsonForm;
  }, [memberData]);
  const activationForm = useMemo(() => (formPath ? loadFormFromPath(formPath) : null), [formPath]);

  const activationErrorMap: Record<string, string> = useMemo(
    () => ({
      400: "bad-request",
      408: "already-activated",
      other: "unknown",
    }),
    [],
  );

  const activationError: string = useMemo(() => {
    if (activationErrorCode && [400, 408].includes(activationErrorCode)) {
      return activationErrorMap[activationErrorCode];
    }
    return activationErrorMap.other;
  }, [activationErrorCode]);

  const { setCurrentPage } = useTrackingStore((state) => ({
    setCurrentPage: state.setCurrentPage,
  }));

  const activationStepByState: Record<string, string> = useMemo(
    () => ({
      verified: "email_verification_confirmed",
      "verify-expired": "email_verification_expired",
      "successful-activation": "validation",
      "activation-failed": "validation_failed",
    }),
    [],
  );

  useEffect(() => {
    if (activationStepByState[state]) {
      setCurrentPage(activationStepByState[state]);
    }
  }, [state]);

  const activationContentMap: Record<string, ReactElement> = {
    "waiting-for-verification": <WaitingForVerificationContent />,
    verified: <VerifiedContent t={t} redirectCallback={redirectCallback} />,
    "verify-expired": <VerifyExpiredContent t={t} />,
    identity: (
      <IdentityContent
        name="identity"
        redirectCallback={redirectCallback}
        initialFormJSON={identityForm}
      />
    ),
    activation: (
      <ActivationFormContent
        name="oneStepRegisterActivation"
        initialFormJSON={activationForm}
        redirectCallback={redirectCallback}
      />
    ),
    "successful-activation": <ActivationSuccessContent t={t} />,
    "activation-failed": (
      <ActivationFailedContent
        t={t}
        redirectCallback={redirectCallback}
        activationError={activationError}
      />
    ),
  };

  return activationContentMap[state];
};
